import React, { useState, useEffect } from 'react';
import { Plus, Package, Lock, Save, Trash2, Image as ImageIcon, Move } from 'lucide-react';
import { toast } from 'react-toastify';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../utils/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import Pagination from '../../components/Pagination';
import ImagePositionPicker from '../components/ImagePositionPicker';
import { Field, Input, Textarea, Select, FileInput, FormActions } from '../components/FormField';
import { useAuth } from '../../context/AuthContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
      <h2 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-3">{title}</h2>
      {children}
    </div>
  );
}

const PALETTE_COLORS = ['#4F8EF7','#10B981','#F59E0B','#8B5CF6','#F43F5E','#F97316','#06B6D4','#84CC16'];
const empty = { title: '', description: '', category: '', tags: '', price: '' };

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-xl px-3 py-2 shadow-xl">
      <p style={{ color: payload[0].payload.color }}>{payload[0].name}: <strong>{payload[0].value}</strong></p>
    </div>
  );
};

export default function AdminProducts() {
  const { can, isMasterAdmin } = useAuth();
  const canCreate = can('products', 'create');
  const canEdit   = can('products', 'edit');
  const canDelete = can('products', 'delete');

  // Dynamic categories from DB
  const [cats, setCats] = useState([]);

  // Hero section state
  const [heroDoc, setHeroDoc]         = useState(null);
  const [heroFiles, setHeroFiles]     = useState([]);
  const [heroLoading, setHeroLoading] = useState(false);
  const [heroImgPos, setHeroImgPos]   = useState('50% 50%');
  const [heroPosLoading, setHeroPosLoading] = useState(false);
  const [heroTitle, setHeroTitle]     = useState('Products');
  const [heroSubtitle, setHeroSubtitle] = useState('Our Catalogue');
  const [heroTitleColor, setHeroTitleColor]       = useState('#FFFFFF');
  const [heroSubtitleColor, setHeroSubtitleColor] = useState('#B8A830');
  const [heroTextLoading, setHeroTextLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [page, setPage]   = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]   = useState(empty);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [catFilter, setCatFilter] = useState('');

  const load = async (p = 1, cat = catFilter) => {
    const params = new URLSearchParams({ page: p, limit: 10 });
    if (cat) params.set('category', cat);
    // Master admin sees all; manager sees all too (to know what's pending)
    const endpoint = isMasterAdmin ? `/products/admin-all?${params}` : `/products/admin-all?${params}`;
    const res = await api.get(endpoint);
    setProducts(res.data.products);
    setPages(res.data.pages);
    setTotal(res.data.total);
    setPage(p);
  };

  const loadAll = async () => {
    const res = await api.get('/products/admin-all?limit=1000');
    setAllProducts(res.data.products || []);
  };

  useEffect(() => {
    load(); loadAll();
    // Fetch categories from DB
    api.get('/categories').then(res => {
      const dbCats = (res.data.categories || []).map((c, i) => ({
        value: c.id,
        label: c.label,
        color: PALETTE_COLORS[i % PALETTE_COLORS.length],
      }));
      setCats(dbCats);
      // Set default category once cats are loaded
      if (dbCats.length > 0) {
        setForm(f => ({ ...f, category: f.category || dbCats[0].value }));
      }
    }).catch(() => {});
    // Fetch hero content
    api.get('/content?page=products&section=hero')
      .then(res => {
        const items = res.data.content || [];
        if (items.length) {
          setHeroDoc(items[0]);
          if (items[0].title)    setHeroTitle(items[0].title);
          if (items[0].subtitle) setHeroSubtitle(items[0].subtitle);
          if (items[0].extraData?.titleColor)    setHeroTitleColor(items[0].extraData.titleColor);
          if (items[0].extraData?.subtitleColor) setHeroSubtitleColor(items[0].extraData.subtitleColor);
          if (items[0].extraData?.objectPosition) setHeroImgPos(items[0].extraData.objectPosition);
        }
      })
      .catch(() => {});
  }, []);

  const uploadHeroImages = async () => {
    if (!heroFiles.length) return;
    setHeroLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'products');
      fd.append('section', 'hero');
      heroFiles.forEach(f => fd.append('images', f));
      const res = await api.post('/content/hero-images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeroDoc(res.data.content);
      setHeroFiles([]);
      toast.success('Hero image uploaded');
    } catch { toast.error('Error uploading image'); }
    finally { setHeroLoading(false); }
  };

  const removeHeroImage = async (idx) => {
    if (!heroDoc?._id) return;
    try {
      const res = await api.delete(`/content/${heroDoc._id}/images/${idx}`);
      setHeroDoc(res.data.content);
      toast.success('Image removed');
    } catch { toast.error('Error removing image'); }
  };

  const saveHeroText = async (e) => {
    e.preventDefault();
    setHeroTextLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'products');
      fd.append('section', 'hero');
      fd.append('title', heroTitle);
      fd.append('subtitle', heroSubtitle);
      fd.append('extraData', JSON.stringify({
        titleColor: heroTitleColor,
        subtitleColor: heroSubtitleColor,
        objectPosition: heroImgPos,
      }));
      const res = await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeroDoc(res.data.content);
      toast.success('Hero title saved');
    } catch { toast.error('Error saving title'); }
    finally { setHeroTextLoading(false); }
  };

  const saveHeroImgPos = async () => {
    if (!heroDoc) return;
    setHeroPosLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'products');
      fd.append('section', 'hero');
      const existing = heroDoc.extraData || {};
      fd.append('extraData', JSON.stringify({ ...existing, objectPosition: heroImgPos }));
      const res = await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeroDoc(res.data.content);
      toast.success('Image position saved');
    } catch { toast.error('Error saving position'); }
    finally { setHeroPosLoading(false); }
  };

  const openCreate = () => {
    const defaultCat = cats.length > 0 ? cats[0].value : '';
    setEditing(null);
    setForm({ title: '', description: '', category: defaultCat, tags: '', price: '' });
    setFiles([]); setPreview(null); setErrors({}); setModal(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, description: p.description, category: p.category, tags: p.tags?.join(', ') || '', price: p.price || '' });
    setFiles([]); setPreview(p.images?.[0] || null); setErrors({}); setModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Product title is required';
    else if (form.title.trim().length < 3) e.title = 'Title must be at least 3 characters';
    if (!form.category || !form.category.trim()) e.category = 'Category is required — please wait for categories to load';
    if (!form.description.trim()) e.description = 'Description is required';
    if (form.price && isNaN(Number(form.price))) e.price = 'Price must be a valid number';
    if (form.price && Number(form.price) < 0) e.price = 'Price cannot be negative';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('category', form.category);
      fd.append('tags', form.tags);
      if (form.price) fd.append('price', form.price);
      files.forEach(f => fd.append('images', f));
      let res;
      if (editing) {
        res = await api.put(`/products/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        res = await api.post('/products', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      if (res.data.pending) {
        toast.info(res.data.message || 'Submitted for approval');
      } else {
        toast.success(editing ? 'Product updated' : 'Product created');
      }
      setModal(false); load(page); loadAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving product');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/products/${id}`); toast.success('Deleted'); load(page); loadAll();
  };

  /* chart data */
  const catCounts = cats.map(c => ({
    name: c.label,
    value: allProducts.filter(p => p.category === c.value).length,
    color: c.color,
  }));

  const columns = [
    { key: 'images', label: 'Image', isImage: true, sortable: false },
    { key: 'title', label: 'Title', isTitle: true,
      render: (v, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800">{v}</span>
          {!row.isActive && (
            <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
              Pending Approval
            </span>
          )}
        </div>
      )
    },
    { key: 'category', label: 'Category',
      render: v => {
        const cat = cats.find(c => c.value === v);
        return <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
          style={{ background: `${cat?.color || '#B8A830'}18`, color: cat?.color || '#B8A830' }}>
          {cat?.label || v}
        </span>;
      }
    },
    { key: 'price', label: 'Price', render: v => v ? <span className="font-semibold text-gray-700">₹{v}</span> : <span className="text-gray-300 text-xs">On request</span> },
    { key: 'createdAt', label: 'Added', render: v => new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) },
  ];

  const summaryFields = cats.map(c => ({
    key: c.value, label: c.label, color: c.color,
    render: data => data.filter(p => p.category === c.value).length,
  }));

  return (
    <div className="space-y-6 pb-8">
      {/* header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Products</h1>
          <p className="text-sm text-gray-400 mt-0.5">{total} total products</p>
        </div>
        <button onClick={openCreate}
          disabled={!canCreate}
          title={!canCreate ? 'No permission to create products' : ''}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: canCreate ? 'linear-gradient(135deg, #B8A830, #d4af37)' : '#9CA3AF' }}>
          {canCreate ? <Plus size={14} /> : <Lock size={14} />} Add Product
        </button>
      </div>

      {/* Product Page Hero Section — master_admin only */}
      {isMasterAdmin && (
        <SectionCard title="Product Page — Hero Section">
          <p className="text-xs text-gray-500 -mt-2">
            Edit the title, subtitle and background image shown at the top of the public Products page.
          </p>

          {/* Text fields */}
          <form onSubmit={saveHeroText} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Page Title" hint='e.g. "Products"'>
                <Input value={heroTitle}
                  onChange={e => setHeroTitle(e.target.value)}
                  placeholder="Products" />
              </Field>
              <Field label="Subtitle / Label" hint='Small text above title, e.g. "Our Catalogue"'>
                <Input value={heroSubtitle}
                  onChange={e => setHeroSubtitle(e.target.value)}
                  placeholder="Our Catalogue" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title Colour" hint="Default: white (#FFFFFF)">
                <div className="flex items-center gap-2">
                  <input type="color" value={heroTitleColor}
                    onChange={e => setHeroTitleColor(e.target.value)}
                    className="w-10 h-9 rounded border border-gray-200 cursor-pointer p-0.5 shrink-0" />
                  <span className="text-sm font-mono text-gray-600">{heroTitleColor}</span>
                  <span className="w-6 h-6 rounded border border-gray-200 ml-auto" style={{ background: heroTitleColor }} />
                </div>
              </Field>
              <Field label="Subtitle Colour" hint="Default: gold (#B8A830)">
                <div className="flex items-center gap-2">
                  <input type="color" value={heroSubtitleColor}
                    onChange={e => setHeroSubtitleColor(e.target.value)}
                    className="w-10 h-9 rounded border border-gray-200 cursor-pointer p-0.5 shrink-0" />
                  <span className="text-sm font-mono text-gray-600">{heroSubtitleColor}</span>
                  <span className="w-6 h-6 rounded border border-gray-200 ml-auto" style={{ background: heroSubtitleColor }} />
                </div>
              </Field>
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={heroTextLoading}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors">
                <Save size={14} /> {heroTextLoading ? 'Saving...' : 'Save Text'}
              </button>
            </div>
          </form>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-600 mb-3">Background Image</p>

            {/* Current images */}
            {heroDoc?.images?.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-3">
                {heroDoc.images.map((url, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={url.startsWith('http') ? url : `${BASE}${url}`}
                      alt={`Hero ${i + 1}`}
                      className="w-32 h-20 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeHeroImage(i)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove image">
                      <Trash2 size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!heroDoc?.images?.length && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 mb-3">
                <ImageIcon size={20} className="text-gray-400" />
                <span className="text-sm text-gray-400">No hero image uploaded yet. Upload one below.</span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={e => setHeroFiles(e.target.files ? [...e.target.files] : [])}
                className="text-sm text-gray-600 flex-1"
              />
              <button
                type="button"
                onClick={uploadHeroImages}
                disabled={!heroFiles.length || heroLoading}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors whitespace-nowrap">
                <Plus size={14} /> {heroLoading ? 'Uploading...' : 'Upload Image'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">Recommended: landscape image, min 1920×400px.</p>
          </div>
        </SectionCard>
      )}

      {/* top row: chart + category filter */}
      <div className="grid lg:grid-cols-4 gap-4">
        {/* pie chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">By Category</p>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={catCounts} cx="50%" cy="50%" innerRadius={35} outerRadius={55}
                paddingAngle={3} dataKey="value" stroke="none">
                {catCounts.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {catCounts.map(c => (
              <div key={c.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                  <span className="text-gray-500 truncate">{c.name}</span>
                </div>
                <span className="font-bold text-gray-700">{c.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* table */}
        <div className="lg:col-span-3">
          {/* category filter pills */}
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={() => { setCatFilter(''); load(1, ''); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors ${!catFilter ? 'bg-yellow-600 text-white border-yellow-600' : 'border-gray-200 text-gray-500 hover:border-yellow-400 hover:text-yellow-600'}`}>
              All
            </button>
            {cats.map(c => (
              <button key={c.value} onClick={() => { setCatFilter(c.value); load(1, c.value); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors ${catFilter === c.value ? 'text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-yellow-400'}`}
                style={catFilter === c.value ? { background: c.color, borderColor: c.color } : {}}>
                {c.label}
              </button>
            ))}
          </div>

          <DataTable
            columns={columns}
            data={products}
            onEdit={canEdit ? openEdit : null}
            onDelete={canDelete ? handleDelete : null}
            searchKeys={['title', 'category']}
            summaryFields={summaryFields}
            defaultView="table"
            subtitle={`Showing ${products.length} of ${total}`}
          />
          <Pagination page={page} pages={pages} onPage={p => load(p)} />
        </div>
      </div>

      {modal && (
        <Modal title={editing ? 'Edit Product' : 'Add New Product'} onClose={() => setModal(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-700">
              Fields marked <span className="text-red-500 font-bold">*</span> are required.
            </div>
            <Field label="Product Title" required hint="Clear, descriptive name" error={errors.title}>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Premium Fabric Swatch Book" error={errors.title} />
            </Field>
            <Field label="Category" required>
              <Select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {cats.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
            </Field>
            <Field label="Description" required error={errors.description}>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the product…" rows={4} error={errors.description} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price (₹)" hint="Leave blank if on request" error={errors.price}>
                <Input type="number" min="0" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}
                  placeholder="0.00" error={errors.price} />
              </Field>
              <Field label="Tags" hint="Comma separated">
                <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })}
                  placeholder="fabric, cotton, premium" />
              </Field>
            </div>
            <FileInput label="Product Images" hint="JPG, PNG — max 5MB each, up to 5 images"
              multiple
              onChange={e => {
                const f = [...e.target.files];
                setFiles(f);
                setPreview(f[0] ? URL.createObjectURL(f[0]) : null);
              }}
              preview={preview} accept="image/*" />
            <FormActions onCancel={() => setModal(false)} loading={loading} submitLabel={editing ? 'Update Product' : 'Add Product'} />
          </form>
        </Modal>
      )}
    </div>
  );
}
